import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Volume2 } from "lucide-react";
import { usePlayer, fmtTime } from "@/context/PlayerContext";
import { Slider } from "@/components/ui/slider";
import { JogWheel } from "./JogWheel";
import { Waveform } from "./Waveform";

export const Eq = ({ active }) => (
  <span className="flex items-end gap-[3px] h-4">
    {[0, 0.2, 0.4, 0.1].map((d, i) => (
      <span key={i} className={`w-[3px] h-full bg-emerald-400 ${active ? "eq-bar" : "scale-y-[0.3] origin-bottom"}`} style={{ animationDelay: `${d}s` }} />
    ))}
  </span>
);

export default function CdjPlayerBar() {
  const { current, playing, time, duration, volume, setVolume, toggle, seek, cue, next, prev } = usePlayer();
  if (!current) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 cdj-body border-t-2 border-emerald-400/40" data-testid="audio-player-bar">
      <div className="max-w-7xl mx-auto px-3 sm:px-8 py-2.5 flex items-center gap-3 sm:gap-5">
        <JogWheel cover={current.cover_url} spinning={playing} size="h-12 w-12 sm:h-14 sm:w-14 shrink-0" testid="player-jog" />
        <Link to={`/uscite/${current.release_id}`} className="min-w-0 w-28 sm:w-44 shrink-0" data-testid="player-current-release-link">
          <p className="text-sm font-bold truncate" data-testid="player-track-title">{current.title}</p>
          <p className="text-xs text-zinc-400 truncate" data-testid="player-track-artist">{current.artist_name}</p>
        </Link>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={cue} aria-label="Cue" data-testid="player-cue-btn" className="hidden sm:grid h-9 w-9 rounded-full border-2 border-amber-500 place-items-center font-mono-tga text-[9px] font-bold text-amber-400 hover:scale-110 transition-transform">CUE</button>
          <button onClick={prev} aria-label="Precedente" data-testid="player-prev-btn" className="hidden sm:block p-1.5 text-zinc-300 hover:text-white"><SkipBack className="h-4 w-4" /></button>
          <button onClick={toggle} aria-label={playing ? "Pausa" : "Play"} data-testid="player-play-pause-btn"
            className={`h-11 w-11 rounded-full border-2 border-emerald-400 bg-[#121423] grid place-items-center hover:scale-110 transition-transform ${playing ? "glow-pulse" : ""}`}>
            {playing ? <Pause className="h-4 w-4 text-emerald-400" /> : <Play className="h-4 w-4 ml-0.5 text-emerald-400" />}
          </button>
          <button onClick={next} aria-label="Successivo" data-testid="player-next-btn" className="hidden sm:block p-1.5 text-zinc-300 hover:text-white"><SkipForward className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 min-w-0 lcd rounded-lg px-3 py-1.5 flex items-center gap-3">
          <span className="hidden sm:block font-mono-tga text-xs text-amber-400 w-10">{fmtTime(time)}</span>
          <div className="flex-1 min-w-0"><Waveform seed={current.id} progress={duration ? time / duration : 0} onSeek={(r) => duration && seek(r * duration)} active={playing} bars={70} height="h-8" variant="lcd" testid="player-waveform" /></div>
          <span className="hidden sm:block font-mono-tga text-xs text-sky-300/80 w-12 text-right">-{fmtTime(Math.max(duration - time, 0))}</span>
        </div>
        <div className="hidden lg:flex items-center gap-2 w-32 shrink-0">
          <Volume2 className="h-4 w-4 text-zinc-300" />
          <Slider value={[volume * 100]} max={100} step={1} onValueChange={(v) => setVolume(v[0] / 100)} data-testid="player-volume-slider" />
        </div>
      </div>
    </div>
  );
}
